import React from "react";
import { useNavigate } from "react-router-dom";
import { BrandLetterhead } from "../components/BrandLetterhead";
import {
  Cloud, Printer, ArrowLeft, Bot, ShieldCheck, Route, LayoutGrid, Rocket, KeyRound,
  ShieldAlert, Siren, MonitorPlay, LifeBuoy, Gauge, ClipboardList, Cpu, Wrench, Bell,
  Download, Sparkles, ClipboardCheck, Wand2, MapPin, Users,
} from "lucide-react";

const SECTIONS = [
  {
    title: "How It Fits — a Companion, Not a Replacement", icon: ShieldCheck,
    items: [
      "UC Engineer Pro sits ON TOP of Webex Control Hub and Cisco CUCM — it orchestrates the migration and troubleshooting work those tools don't, and does not duplicate their native admin/inventory functions",
      "What we add: a unified, gated per-site cutover runbook (CUCM + Webex + carrier/PSTN + network) with GO/NO-GO sign-off, waves, an estate scoreboard, an AI field-engineer copilot, consolidated compliance trackers with reminders, and client-ready evidence/PDF exports",
      "What we defer to the platforms: device/user/DN cloud-readiness analysis → Webex Control Hub Migration Insights; number, license & location management → Control Hub; bulk provisioning, RTMT & AXL → CUCM",
      "Where the platforms already hold the data, we IMPORT it (read-only) to drive the plan — we never try to re-implement Control Hub or CUCM",
    ],
  },
  {
    title: "Diagnostics & AI Support", icon: Bot,
    items: [
      "Interactive Diagnostic Wizard covering CUCM / SIP / PRI on-prem and Webex / Teams cloud troubleshooting flows",
      "AI Assistant (Claude Sonnet 4.6) with streaming answers, exact Cisco IOS CLI, RTMT paths and 'good vs bad' output",
      "'Ask AI about this step' deep-links that pass the current symptom context straight into the assistant",
      "AI-powered Firmware / Bug Insight for CUCM, Webex MPP and Room devices, with cached insights",
    ],
  },
  {
    title: "Readiness & Health", icon: ShieldCheck,
    items: [
      "Health Checks for CUCM cluster and Cloud (Webex Calling & Teams) readiness playbooks",
      "Readiness scoring (% pass/fail) rolled up per site",
      "Site 360 — a single pane unifying migration status, licenses, certs, E911 and readiness per location",
    ],
  },
  {
    title: "PPDIO Migration Runbook", icon: Route,
    items: [
      "Per-site, independently gated runbook across all 7 PPDIO phases (Prepare → Plan → Design → Implement → Cutover → Optimize → Decommission), 209 tasks",
      "Server-enforced GO/NO-GO gates — a phase cannot advance until its tasks pass and gates are signed off",
      "Discovery Questionnaire captured per site (existing PSTN gateway, Local Gateway, users, DIDs) that drives the plan",
      "Prefill from Discovery — auto-ticks inventory tasks from your E911, Rooms and site data",
      "Per-task 'how-to verify' commands with exactly WHERE to run them (CUCM CLI, RTMT, CUBE/IOS, Webex Control Hub)",
      "AI Migration Guide — an on-demand walk-through that produces the exact commands for any step and can drop them into a task's evidence",
    ],
  },
  {
    title: "Import Your Existing Cisco Estate", icon: Download,
    items: [
      "Import from CUCM — upload or paste a CUCM phone export (BAT / RTMT / AXL SQL) and the app auto-aggregates by model, keeps the lowest firmware seen, and instantly flags every model against the compatibility matrix",
      "Bulk CSV import for Licenses, Certificates and E911 records, with downloadable sample templates and smart column mapping (header synonyms auto-detected)",
      "Read-only by design — nothing is written back to Cisco; engineers pull their customer's data in to plan the cutover",
      "Roadmap: Webex Control Hub (read-only OAuth) target-org validation and an inside-network read-only AXL live pull feeding the same engine",
    ],
  },
  {
    title: "Estate, Waves & Reporting", icon: LayoutGrid,
    items: [
      "Estate Scoreboard — colour-coded grid of every location's migration, gate progress, E911, rooms, readiness and device status",
      "One-click Cutover Readiness Report (PDF) — estate-wide summary of every site's unsupported devices to replace, firmware gaps to upgrade, and items to review before cutover",
      "Migration waves — group sites into named waves; the scoreboard groups and rolls up by wave",
      "Auto-suggest waves — proposes a size/complexity-based wave plan (AI-powered when enabled) you can apply in one click",
      "Runbook momentum trends — a daily per-wave snapshot charting progress over time",
      "Wave Cutover runsheet — a shared cutover-day checklist per wave with a wave-level sign-off gated on every site's Cutover gate",
      "Branded one-page PDF exports for the Estate Scoreboard and a full estate audit (all trackers + runbook status)",
      "Universal print/PDF on every tracking page",
    ],
  },
  {
    title: "Compliance & Lifecycle Trackers", icon: ClipboardCheck,
    items: [
      "Certificate expiry tracker (CUCM / Tomcat / cluster certs)",
      "License tracker with Webex sync and coloured renewal alerts",
      "E911 compliance — ERL / ELIN mapping and status per site",
      "Room Collaboration inventory for Webex / Teams endpoints",
      "DR & Failover runbook with test logs and recovery checkpoints",
      "QoS policy builder for WAN/LAN validation",
      "ITIL Change Log with approvals and post-implementation review",
    ],
  },
  {
    title: "Free vs Pro — Plans & Entitlements", icon: Users,
    items: [
      "Private multi-tenant workspaces — every account's estate data, runbooks and trackers are fully isolated",
      "Self-serve signup with email/password or Google SSO — try the platform free, no credit card required (no full-access time trial)",
      "Free tier (troubleshooting-first): full Diagnostic Wizard & guides, 5 AI troubleshooting requests/day, 2 demo sites, and read-only preview of the migration & CUCM 15 workflows",
      "Pro tier ($19.99/mo or $149/yr, all future version updates included free): unlimited sites, full production migration & CUCM 15 runbooks, waves & cutover execution, GO/NO-GO gates, number porting, estate management, bulk import, PDF/report exports, advanced AI and team seats",
      "Server-enforced entitlements — Pro-only actions are gated on the backend (not just hidden), with an in-app upgrade prompt and a monthly/annual Billing page via Stripe",
      "Team seats — the workspace owner invites colleagues by email with a private one-click join link",
    ],
  },
  {
    title: "Platform, Experience & Delivery", icon: Sparkles,
    items: [
      "Dual runbook tracks — CUCM → Webex Calling migration AND Upgrade to CUCM 15, each independently gated per site",
      "Per-site Device Compatibility gate — unsupported models, End-of-Life gear and below-minimum firmware block cutover until resolved; auto-populated from your CUCM import",
      "Grouped, collapsible sidebar — 25+ tools organised into categories (Assess, Migrate, Operate & Compliance, Tools & AI, Reports, Workspace) with an accordion and a collapse-to-icons rail",
      "Admin analytics — visitor IP, device/user-agent and geolocation capture with a reporting dashboard",
      "Geo-blocking middleware for sanctioned regions, plus Privacy, Terms and cookie-consent compliance pages",
      "Live notifications for expiring certificates & licenses, E911 gaps, gate approvals and device alerts",
      "Installable Progressive Web App (Add to Home Screen) — iOS safe-area aware, auto-updates to the latest on every launch",
      "Get Started kickoff — a guided 6-step onboarding to take a site from discovery to cutover",
      "Tools: bulk MAC parser for BAT provisioning and more",
    ],
  },
];

export default function Capabilities() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="print:hidden border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate(-1)} data-testid="cap-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button onClick={() => window.print()} data-testid="cap-print-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Download className="h-4 w-4" /> Export / Download PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-6 sm:p-10" data-testid="capabilities-doc">
        <BrandLetterhead title="UC Engineer White Paper" subtitle="Complete capability reference · AI-Powered UC Engineering Workspace" />
        <p className="text-[11px] uppercase tracking-widest text-zinc-600 mt-2 font-semibold">Formerly known as “Capabilities”</p>

        <p className="text-zinc-400 mt-4 text-[13px] leading-relaxed">
          UC Engineer Pro is an AI-powered Cisco &amp; Cloud UC engineering workspace — <span className="text-zinc-200 font-medium">Troubleshoot · Configure · Migrate · Validate</span>.
          It is roughly half live field-engineering/troubleshooting workspace and half production migration &amp; upgrade workspace,
          spanning AI-assisted diagnostics and readiness, a strictly gated per-site PPDIO runbook, direct import of a customer's
          existing Cisco estate, wave-based cutovers, an estate-wide scoreboard, and a one-click cutover readiness report.
          This white paper documents the complete capability set across every module.
        </p>

        <div className="mt-6 space-y-6">
          {SECTIONS.map((sec) => (
            <div key={sec.title} className="break-inside-avoid">
              <div className="flex items-center gap-2 text-blue-400">
                <sec.icon className="h-4 w-4" />
                <h2 className="font-heading font-bold text-white text-base">{sec.title}</h2>
              </div>
              <ul className="mt-2 space-y-1.5">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-zinc-300 leading-snug">
                    <span className="text-blue-500 mt-1 shrink-0">▹</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-zinc-800 text-[11px] text-zinc-600">
          Note: AI features (Migration Guide, firmware insight, wave planner) run on the Emergent Universal Key.
          Some third-party checks describe where to run vendor commands (CUCM, RTMT, CUBE, Webex Control Hub).
        </div>
      </div>
    </div>
  );
}
